import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable, View } from 'react-native';
import { Menu } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { themedStyleSheet } from '../../themes/themedStyleSheet';
import { CloseButton } from '../crop-circle/CloseButton';
import { Icon } from '../crop-circle/icons';
import { spacing, tokens } from '../crop-circle/tokens';
import { ActionMenuList, ActionMenuRow } from '../issue/messageActionMenu';
import {
  ASSET_CHAT_STREAM_FILTERS,
  type AssetChatStreamFilter,
} from '@gripp/shared-logic';
import { Button } from '../crop-circle/Button';
import { TypeIcon } from '../crop-circle/Components/Type';

export type AssetChatToolbarProps = {
  onCallDealer?: () => void;
  onToggleFilters: () => void;
  filtersOpen: boolean;
  streamFilter: AssetChatStreamFilter;
  children?: ReactNode;
};

export const AssetChatToolbar = ({
  onCallDealer,
  onToggleFilters,
  filtersOpen,
  streamFilter,
  children,
}: AssetChatToolbarProps) => {
  const { t } = useTranslation();
  return (
    <View style={styles.toolbar}>
      {onCallDealer && (
        <Button
          onPress={onCallDealer}
          style={styles.iconButton}
          accessibilityLabel={t('assets.callDealer')}
          testID="asset-chat-panel-call-button"
          leftIcon={
            <Icon name="PhoneCall" size={20} color={Colors.role.textStrong} />
          }
        />
      )}
      <View>
        <Button
          onPress={onToggleFilters}
          style={styles.iconButton}
          accessibilityLabel={t('chat.filters.toggleLabel')}
          accessibilityState={{ expanded: filtersOpen }}
          aria-expanded={filtersOpen}
          testID="asset-chat-panel-filter-toggle"
          leftIcon={
            <Icon
              name="Filter"
              size={20}
              color={
                filtersOpen || streamFilter !== 'all'
                  ? Colors.role.brandOnSurface
                  : Colors.role.textStrong
              }
            />
          }
        />
        {streamFilter !== 'all' && (
          <View
            pointerEvents="none"
            style={styles.filterDot}
            testID="asset-chat-panel-filter-active"
          />
        )}
      </View>
      {children}
    </View>
  );
};

export const AssetChatFilters = ({
  selected,
  onSelect,
  largeText = false,
}: {
  selected: AssetChatStreamFilter;
  onSelect: (filter: AssetChatStreamFilter) => void;
  /** Native host supplies a horizontal scroller for enlarged text. */
  largeText?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <View
      style={[
        styles.filters,
        largeText && Platform.OS !== 'web' && { flexWrap: 'nowrap' },
      ]}
    >
      {ASSET_CHAT_STREAM_FILTERS.map((filter) => (
        <Button
          key={filter}
          state={selected === filter ? 'active' : 'default'}
          style={{
            ...styles.filterChip,
            ...(selected !== filter && {
              backgroundColor: Colors.role.surfaceRaised,
            }),
            ...(largeText &&
              Platform.OS !== 'web' && {
                height: 'auto',
                minHeight: 30,
                paddingVertical: spacing.xs,
              }),
          }}
          textStyle={
            filter === 'urgent' || filter === 'pending' || filter === 'solved'
              ? styles.filterStatusLabel
              : styles.filterLabel
          }
          leftIcon={
            filter === 'urgent' ||
            filter === 'pending' ||
            filter === 'solved' ? (
              <TypeIcon variant={filter} size={16} />
            ) : undefined
          }
          onPress={() => onSelect(filter)}
          accessibilityLabel={t(`chat.filters.${filter}`)}
          accessibilityState={{ selected: selected === filter }}
          aria-pressed={selected === filter}
          testID={`asset-chat-panel-filter-${filter}`}
        >
          {t(`chat.filters.${filter}`)}
        </Button>
      ))}
    </View>
  );
};

export type AssetChatHeaderMenuProps = {
  /**
   * Opens the band to the Share panel. Absent hides the whole entry (mirrors
   * `MessageActionMenu`'s convention: an absent handler hides its row), and
   * the caller must not render this component at all when nothing would be
   * offered — see the doc comment below.
   */
  onShare?: () => void;
  /**
   * Whether the band this menu opens is currently showing. Distinct from the
   * menu's own `visible` state (whether the dropdown itself is open) — the
   * trigger stays highlighted for as long as the band is open, not just
   * while the menu is, so the kebab reads as a toggle rather than going
   * visually inert the moment the dropdown closes.
   */
  active?: boolean;
  testID?: string;
};

/**
 * The chat header's kebab: a Crop Circle overflow menu (mirrors
 * `MessageActionMenu`'s Menu/`ActionMenuRow` treatment — same component,
 * reused rather than re-implemented) offering Share, which opens the band
 * beneath the header exactly as the kebab used to on its own (Share panel
 * plus the collapsed Participants disclosure, both unchanged).
 *
 * The menu itself is the extension point: Phase 4's stream-filter band
 * slots in as a second `onFilter?`-style prop and a second `ActionMenuRow`
 * here, with the caller (assetChatPanel.tsx) owning which one band is open —
 * this component only decides which row to show, never which band.
 *
 * `onShare` is the ONLY action today, so the caller must render this
 * component only when `onShare` is defined (matching `MessageActionMenu`'s
 * "the caller mounts it only when at least one action is available") —
 * otherwise the menu opens empty, which is worse than not showing a kebab.
 */
export const AssetChatHeaderMenu = ({
  onShare,
  active = false,
  testID,
}: AssetChatHeaderMenuProps) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  const trigger = (
    <Pressable
      style={[styles.toggle, (visible || active) && styles.toggleActive]}
      onPress={() => setVisible(true)}
      accessibilityRole="button"
      accessibilityState={{ expanded: visible }}
      accessibilityLabel={t('chat.band.toggleLabel')}
      testID={testID}
    >
      <Icon name="More Vertical" size={20} color={Colors.role.textStrong} />
    </Pressable>
  );

  return (
    <Menu
      visible={visible}
      onDismiss={() => setVisible(false)}
      contentStyle={styles.menu}
      anchor={trigger}
    >
      <ActionMenuList>
        {onShare && (
          <ActionMenuRow
            iconName="Share"
            label={t('chat.band.menu.share')}
            accessibilityLabel={t('chat.band.menu.share')}
            onPress={() => {
              setVisible(false);
              onShare();
            }}
            testID={testID ? `${testID}-share` : undefined}
          />
        )}
      </ActionMenuList>
    </Menu>
  );
};

export type AssetChatBandProps = {
  /** Whether the band is toggled open. Renders nothing when false. */
  visible: boolean;
  variant?: 'default' | 'filters';
  /**
   * Ordered slot content — Share sits above Participants (task 3.7 adds it),
   * this component just stacks whatever it's given. Nothing here assumes
   * Participants is the only child.
   */
  children: ReactNode;
  /**
   * Dismisses the band. Renders the band's own close control when present —
   * absent renders no close row at all, same convention as `onShare` above.
   * Lives on the band itself (not inside a child slot) so every band this
   * component ever hosts — Share/Participants today, Phase 4's filter band
   * later — gets the same dismissal for free.
   */
  onClose?: () => void;
  testID?: string;
};

/**
 * The inline band the chat header's kebab toggles: sits beneath the header,
 * inside the chat column — not a new stacked panel, and not a `ScrollView`
 * (the column already disables scroll for the feed beneath it).
 *
 * The close control is the Crop Circle `CloseButton` (the same X-in-a-circle
 * atom `alertLogicInput.tsx` uses to dismiss an inline row), in the band's
 * top-right corner — not a header bar with a title, since this band has no
 * title of its own (Share has no heading; Participants carries its own
 * label on its disclosure row) and inventing one just to hang a back-chevron
 * off it (the `pulseFilterPanel.tsx` treatment) would add chrome the content
 * doesn't call for. A one-line close row above the existing content.
 */
export const AssetChatBand = ({
  visible,
  variant = 'default',
  children,
  onClose,
  testID,
}: AssetChatBandProps) => {
  const { t } = useTranslation();
  if (!visible) return null;
  return (
    <View
      style={[styles.band, variant === 'filters' && styles.filterBand]}
      testID={testID}
    >
      {onClose && (
        <View style={styles.closeRow}>
          <CloseButton
            onPress={onClose}
            accessibilityLabel={t('common.close')}
            testID={testID ? `${testID}-close` : undefined}
          />
        </View>
      )}
      {children}
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  iconButton: {
    width: 32,
    minWidth: 32,
    paddingHorizontal: 0,
    backgroundColor: 'transparent',
  },
  filters: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: spacing.sm,
  },
  filterChip: { minWidth: 0, height: 30, paddingHorizontal: spacing.md },
  filterLabel: { fontSize: 14, lineHeight: 18 },
  filterStatusLabel: { fontSize: 14, lineHeight: 18, marginLeft: spacing.xxs },
  filterBand: {
    backgroundColor: role.surface,
    borderBottomColor: role.borderSection,
    paddingVertical: spacing.md,
  },
  filterDot: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: spacing.sm,
    height: spacing.sm,
    borderRadius: tokens.borderRadius.full,
    backgroundColor: tokens.colors.brand.core,
  },
  toggle: {
    width: 32,
    height: 32,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: role.surfaceHover,
  },
  // Crop Circle overflow menu surface — same treatment as
  // `messageActionMenu.tsx`'s own `menu` style, duplicated rather than
  // imported since each component owns its themedStyleSheet in this
  // codebase; kept identical on purpose so the two menus read as one system.
  menu: {
    backgroundColor: role.surfacePopover,
    borderRadius: tokens.borderRadius.md,
    paddingVertical: 0,
  },
  band: {
    borderBottomWidth: 1,
    borderBottomColor: role.borderSubtle,
    backgroundColor: role.surfaceRaised,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    gap: spacing.sm,
  },
  closeRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
}));
